import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { Photo } from './PhotoGrid';

interface PhotoDetailProps {
  photo: Photo;
  nextPhotoId?: string;
  prevPhotoId?: string;
  onClose: () => void;
}

const PhotoDetail: React.FC<PhotoDetailProps> = ({ 
  photo, 
  nextPhotoId, 
  prevPhotoId, 
  onClose 
}) => {
  const navigate = useNavigate();

  const goToNextPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (nextPhotoId) {
      navigate(`/photo/${nextPhotoId}`);
    }
  };

  const goToPrevPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (prevPhotoId) {
      navigate(`/photo/${prevPhotoId}`);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && nextPhotoId) navigate(`/photo/${nextPhotoId}`);
      if (e.key === 'ArrowLeft' && prevPhotoId) navigate(`/photo/${prevPhotoId}`);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [onClose, navigate, nextPhotoId, prevPhotoId]);

  return (
    <div 
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center animate-fade-in"
      onClick={onClose}
    >
      <button
        className="absolute top-4 right-4 md:top-6 md:right-6 text-white/80 hover:text-white transition-colors"
        onClick={onClose}
        aria-label="Close"
      >
        <X className="h-6 w-6" />
      </button>
      
      <div 
        className="w-full h-full md:w-auto md:h-auto md:max-w-[90vw] md:max-h-[85vh] flex flex-col md:flex-row items-center justify-center animate-zoom-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full md:w-3/5 flex items-center justify-center p-4 md:p-8">
          <img 
            src={photo.url} 
            alt={photo.title} 
            className="object-contain rounded-md md:max-w-[60vw] md:max-h-[75vh] max-w-[100vw] max-h-[75vh]"
            loading="eager"
          />
        </div>
        <div className="w-full md:w-2/5 p-4 text-center text-white">
          <h2 className="font-playfair text-xl">{photo.title}</h2>
          {photo.description && (
            <p className="mt-2 text-white/80 max-w-lg">{photo.description}</p>
          )}
          {photo.category && (
            <span className="inline-block mt-3 text-xs uppercase tracking-wide px-3 py-1 rounded-full border border-white/30">
              {photo.category}
            </span>
          )}
        </div>
      </div>
      
      {prevPhotoId && (
        <button
          className="absolute left-4 md:left-6 top-1/2 transform -translate-y-1/2 text-white/80 hover:text-white transition-colors"
          onClick={goToPrevPhoto}
          aria-label="Previous photo"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
      )}
      
      {nextPhotoId && (
        <button
          className="absolute right-4 md:right-6 top-1/2 transform -translate-y-1/2 text-white/80 hover:text-white transition-colors"
          onClick={goToNextPhoto}
          aria-label="Next photo"
        >
          <ArrowRight className="h-6 w-6" />
        </button>
      )}
    </div>
  );
};

export default PhotoDetail;
